import { isDefined } from '@/composables/utils'
import { LoadStatus } from '@/enums/LoadStatus'
import { Role } from '@/enums/Role'
import { SettingsTab } from '@/enums/SettingsTab'
import { StoreId } from '@/enums/StoreId'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IAdminsStore } from '@/interfaces/api/IAdminsStore'
import type { IDatasetStore } from '@/interfaces/api/IDatasetStore'
import type { IDevicesStore } from '@/interfaces/api/IDevicesStore'
import type { AdminRecord } from '@/models/AdminRecord'
import type { DeviceRecord } from '@/models/DeviceRecord'
import { getAuthInstance, type Auth0User } from '@fusion/auth-v3'
import { FusionMixins } from '@fusion/saas-components-v3'
import axios from 'axios'
import { defineStore } from 'pinia'
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDisplay, type DisplayInstance } from 'vuetify'
import { useSelfSchedulerStore } from '../db/SelfSchedulerStore'
import { TableStoreFactory } from '../db/TableStoreFactory'
import { useSettingsStore } from './SettingsStore'

const RESFIT_ROLE = 'TSIL-RP'

export const useAppStore = defineStore(StoreId.Application, () => {
  // STORES, IMPORTS, & COMPOSABLES

  const adminsStore = TableStoreFactory.get(TableStoreId.Admins) as IAdminsStore
  const auth0 = getAuthInstance()
  const datasetsStore = TableStoreFactory.get(TableStoreId.Datasets) as IDatasetStore
  const devicesStore = TableStoreFactory.get(TableStoreId.Devices) as IDevicesStore
  const display: DisplayInstance = useDisplay()
  const { t } = useI18n({ useScope: 'global' })
  const router = useRouter()
  const routes = router.getRoutes()
  const selfSchedulerStore = useSelfSchedulerStore()

  // CONSTANTS
  const logoSrc = `./img/tsi_link_logo.svg`
  const { drawer, drawerPermanent, drawerToggle } = FusionMixins.drawer()

  // REACTIVE VARIABLES
  const activationCode = ref<string>('')
  const admins = ref<Array<AdminRecord>>([])
  const isPrinting = ref<boolean>(false)
  const itemsPerPage = ref<number>(5)
  const loadStatus = ref<LoadStatus>(LoadStatus.Loading)
  const devicesExpirations = ref<Array<DeviceRecord>>([])
  const currentAdmin = ref<AdminRecord | null>(null)
  const adminHeaders = [
    {
      title: t('fields.admins.lastName'),
      key: 'lastName',
      nowrap: true,
      headerProps: { searchField: true }
    },
    {
      title: t('fields.admins.firstName'),
      key: 'firstName',
      nowrap: true,
      headerProps: { searchField: true }
    },
    {
      title: t('fields.admins.email'),
      key: 'email',
      nowrap: true,
      headerProps: { searchField: true }
    }
  ]

  const showFeedbackDialog = ref<boolean>(false)
  const showDeviceAlert = ref<boolean>(false)

  // LIFECYCLE HOOKS
  onBeforeMount(() => {
    window.addEventListener('beforeprint', () => {
      isPrinting.value = true
    })
    window.addEventListener('afterprint', () => {
      isPrinting.value = false
    })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('beforeprint', () => {
      isPrinting.value = true
    })
    window.removeEventListener('afterprint', () => {
      isPrinting.value = false
    })
  })

  // WATCHERS

  // COMPUTED
  /**
   * The current application version.
   * This is set by the build process and is used to display the version in the footer
   */
  const appVersion = computed((): string => {
    return APP_VERSION
  })

  /**
   * The current authenticated user.
   * This is used to display the user's information in the header and for authentication checks.
   * If the user is not authenticated, an empty object is returned.
   */
  const auth0User = computed((): Auth0User => {
    if (isDefined(auth0?.user)) {
      return auth0.user!
    }
    return {}
  })

  /**
   * Flag that indicates if the application has a drawer.
   * This is used to determine if the drawer should be displayed in the header.
   * The drawer is only displayed if the user is authenticated and not in self-schedule or preview mode.
   * It is also dependent on the display size and whether the drawer is permanent.
   * @returns {boolean} True if the application has a drawer, false otherwise.
   */
  const hasDrawer = computed((): boolean => {
    return (
      loadStatus.value === LoadStatus.AuthenticatedViaAuth0 &&
      !isSelfSchedule.value &&
      !isPreview.value
    )
  })

  /**
   * Flag that indicates if the user is authenticated.
   * This is true if the user is authenticated via Auth0 or via a scheduling key.
   * It is used to determine if the user can access the application and its features.
   * @returns {boolean} True if the user is authenticated, false otherwise.
   */
  const isAuthenticated = computed(() => {
    return (
      loadStatus.value === LoadStatus.AuthenticatedViaAuth0 ||
      loadStatus.value === LoadStatus.AuthenticatedViaSchedulingKey
    )
  })

  /**
   * Flag that indicates if the application is currently loading.
   * This is used to show a loading indicator while the application is fetching data or performing actions.
   * It is true if the load status is Loading, otherwise false.
   * @returns {boolean} True if the application is loading, false otherwise.
   */
  const isLoading = computed(() => {
    return loadStatus.value === LoadStatus.Loading
  })

  /**
   * Flag that indicates if the application is in printing mode.
   * This is used to apply specific styles or behaviors when printing the application.
   * It is true if the isPrinting reactive variable is true, otherwise false.
   * @returns {boolean} True if the application is in printing mode, false otherwise.
   */
  const isPreview = computed(() => {
    return router.currentRoute.value.name === 'preview'
  })

  /**
   * Flag that indicates if the current user is a Program Administrator.
   * This is used to determine if the user has access to certain features or settings that are
   * only available to Program Administrators.
   * It checks if the current admin's role is ProgramAdmin.
   * @returns {boolean} True if the user is a Program Administrator, false otherwise.
   */
  const isProgramAdmin = computed(() => {
    return currentAdmin.value?.role === Role.ProgramAdmin
  })

  /**
   * Flag that indicates if the current route is the self-schedule route.
   * This is used to determine if the application is in self-schedule mode.
   * @returns {boolean} True if the application is in self-schedule mode, false otherwise.
   */
  const isSelfSchedule = computed(() => {
    return router.currentRoute.value.name === 'selfSchedule'
  })

  /**
   * Flag that indicates if the application is in printing mode.
   * This is used to apply specific styles or behaviors when printing the application.
   * It is true if the isPrinting reactive variable is true, otherwise false.
   * @returns {boolean} True if the application is in printing mode, false otherwise.
   */
  const showAuthenticator = computed(() => {
    return !isSelfSchedule.value && !isLoading.value
  })

  /**
   * Flag that indicates if the burger menu should be displayed.
   * This is used to show or hide the burger menu in the header.
   * The burger menu is displayed if the application has a drawer, the display size is small or smaller,
   * the drawer is not permanent, and the application is not in loading or preview mode.
   * @returns {boolean} True if the burger menu should be displayed, false otherwise
   */
  const showBurger = computed(() => {
    return (
      hasDrawer.value &&
      display.lgAndDown.value &&
      !drawerPermanent.value &&
      !isLoading.value &&
      !isPreview.value
    )
  })

  /**
   * Flag that indicates if the page size selector should be displayed.
   * This is used to show or hide the page size selector in the header.
   * The page size selector is displayed if the application is not in printing
   * mode and the Auth0 client ID is one of the specified client IDs.
   * @returns {boolean} True if the page size selector should be displayed, false otherwise.
   */
  const showPageSize = computed(() => {
    const clientIds = ['fjlJsIzjifthcYhkFnT8D4YQtKqUjImV', '4cUuWaxbbI31AiVNNPfNpsT2eTa98ZiZ']
    const clientId = APP_ENV.VITE_APP_AUTH0_CLIENT_ID
    return !isPrinting.value && clientIds.includes(clientId)
  })

  // FUNCTIONS
  /**
   * Cancels any pending device alert.
   */
  const cancelDeviceAlert = () => {
    showDeviceAlert.value = false
  }

  /**
   * Closes the feedback dialog.
   */
  const closeFeedbackDialog = () => {
    showFeedbackDialog.value = false
  }

  /**
   * Navigates to the Devices settings page.
   * This function is called when the user clicks on the "Go to Devices" button in the device alert.
   * It hides the device alert and navigates to the Devices settings tab.
   * If the user is not authenticated, it will not perform any action.
   */
  const gotoDevices = () => {
    showDeviceAlert.value = false
    const settingsStore = useSettingsStore()
    settingsStore.selectedTab = SettingsTab.Devices
    router.push('/settings')
  }

  /**
   * Checks for any expired devices and displays an alert if any are found.
   * This function is called when the application is loaded and the user is a Program Administrator.
   * If there are no expired devices, it will not display any alert.
   */
  const checkDeviceExpirations = async () => {
    const response = await devicesStore.getDeviceExpirations()
    const totalExpired = response.data?.results ? response.data.results.length : 0
    if (!response.error && totalExpired > 0 && !isPreview.value) {
      devicesExpirations.value = response.data.results as DeviceRecord[]
      showDeviceAlert.value = true
    }
  }

  /**
   * Checks if the user has an active subscription.
   * @returns {Promise<boolean>} True if the user has an active subscription, false otherwise.
   */
  const checkSubscription = async (): Promise<boolean> => {
    // If the user is not in the ResFit role then they need to activate a subscription.
    if (auth0.user && auth0.user['https://tsi.com/roles'].indexOf(RESFIT_ROLE) === -1) {
      return false
    }

    // Get the user's list of subscriptions and check if they have an active ResFit subscription.
    const response = await axios.get(`${APP_ENV.VITE_APP_SUBS_HOST}/subscriptions/user`)
    if (response.status === 200) {
      const subscription = response.data.find((sub: any) => {
        return sub.id === RESFIT_ROLE
      })
      const activationIds: Array<string> = subscription?.activationIds ?? []
      return activationIds.some((activationId: string, index: number) => {
        const activationDate = subscription.activationDate[index]
        const expires = subscription.expires[index]
        const now = new Date()
        if (
          activationDate !== null &&
          new Date(activationDate) <= now &&
          (expires === null || new Date(expires) > now)
        ) {
          return true
        }
        activationCode.value = activationId
        return false
      })
    }
    return false
  }

  const loadApplication = async () => {
    let currentAttempt = 0
    const intervalId = setInterval(async () => {
      currentAttempt++

      // Attempt to authenticate. If it never happens then timeout.
      if (currentAttempt >= 100) {
        clearInterval(intervalId)
        loadStatus.value = LoadStatus.AuthTimeout
        return
      }

      // If authenticated, then check if authorized.
      if (auth0?.isAuthenticated) {
        clearInterval(intervalId)

        // Email must be verified
        if (!auth0.user?.email_verified) {
          loadStatus.value = LoadStatus.UnverifiedEmail
          return
        }

        // Setup Pendo to track usage.
        pendo?.initialize({
          visitor: {
            id: auth0.getAccountId(),
            email: auth0.user?.email ?? null,
            firstName: auth0.user?.given_name ?? null,
            lastName: auth0.user?.family_name ?? null
          },
          account: {
            id: auth0.getAccountId()
          }
        })

        // The user must have an active subscription if not they need to request access.
        const hasSubscription = await checkSubscription()
        if (!hasSubscription) {
          loadStatus.value = LoadStatus.NoSubscription
          return
        }

        // Load in all enabled dataset options.
        const success = await datasetsStore.loadEnabled()
        if (!success) {
          loadStatus.value = LoadStatus.ErrorLoadingDatasets
          return
        }

        // Load in all program administrators.
        admins.value = await adminsStore.loadAdmins()

        // If this is the first login ever then initialize the account.
        const initialized = await datasetsStore.initialize(auth0.user)

        // If the account was not initialized display an error.
        if (!initialized) {
          loadStatus.value = LoadStatus.ErrorInitialization
          return
        }

        // The user must be a Program Administrator or a Test Administrator.
        currentAdmin.value = await adminsStore.getAdminBySubOrEmail(auth0.user)
        if (!currentAdmin.value) {
          loadStatus.value = LoadStatus.NotAuthorizedForAuth0
          return
        }

        // Synchronize admin info with the database.
        await adminsStore.syncAdminInfo(currentAdmin.value.id!, auth0.user)

        // Finally load account settings, on failure display an error.
        const settingsStore = useSettingsStore()
        const settingsLoaded = await settingsStore.load()
        if (!settingsLoaded) {
          loadStatus.value = LoadStatus.ErrorLoadingSettings
          return
        }

        // Show any expired devices.
        if (isProgramAdmin.value) {
          await checkDeviceExpirations()
        }
        loadStatus.value = LoadStatus.AuthenticatedViaAuth0
      }
    }, 500)
  }

  const loadSelfScheduler = async () => {
    const results = await selfSchedulerStore.authenticateAndLoad()
    if (results) {
      loadStatus.value = LoadStatus.AuthenticatedViaSchedulingKey
    } else {
      loadStatus.value = LoadStatus.NotAuthorizedForScheduler
    }
  }

  const openFeedbackDialog = () => {
    showFeedbackDialog.value = true
  }

  return {
    activationCode,
    admins,
    adminHeaders,
    currentAdmin,
    appVersion,
    auth0User,
    loadStatus,
    devicesExpirations,
    drawer,
    drawerPermanent,
    hasDrawer,
    isAuthenticated,
    isLoading,
    isPrinting,
    isProgramAdmin,
    isSelfSchedule,
    itemsPerPage,
    logoSrc,
    showAuthenticator,
    showDeviceAlert,
    showBurger,
    showFeedbackDialog,
    showPageSize,
    routes,
    cancelDeviceAlert,
    closeFeedbackDialog,
    drawerToggle,
    gotoDevices,
    loadApplication,
    loadSelfScheduler,
    openFeedbackDialog
  }
})
